import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';

import BreadcrumbNav from '../../ui/breadcrumb';
import FormNav from '../../ui/form-elements/form-page/form-nav';
import { api } from '../../../services/api';
import FormPage from '../../ui/form-elements/form-page/form-page';
import Modal from '../../base/modals/base-modal';

import './strategy-creation.scss';
import SetUpPage from './set-up-page';
import TargetingPage from './targeting-page';
import ObjectivesPage from './objectives-page';

const strategyDefaultValues = {
  strategy_name: null,
  strategy_description: null,
  strategy_budget_amount: null,
  strategy_budget_type: 'lifetime',
  strategy_datetime_start: null,
  strategy_datetime_end: null,
  strategy_pricing_model: null,
  strategy_payment_model: null,
  strategy_utm_query_string: null,
  strategy_visibility: 'public',
  strategy_roi_goal: null,
  strategy_bid: null,
  strategy_publisher_epc: null,
  strategy_publisher_roi_target: null,
  strategy_cogs: null,
};

const StrategyCreation = props => {
  const { merchantId, strategyGroupId, strategyId } = useParams();
  const history = useHistory();
  const [currentStrategyId, setCurrentStrategyId] = useState(strategyId);
  const [currentPage, setCurrentPage] = useState(0);
  const [strategyGroupData, setStrategyGroupData] = useState();
  const [currentStrategy, setCurrentStrategy] = useState(strategyDefaultValues);
  const [merchantPricingModel, setMerchantPricingModel] = useState(null);

  //  Temp save state when creating new Campaign
  const [selectedObjective, setSelectedObjective] = useState(null);

  const merchantEndpoint = `/api/v0/merchants/${merchantId}/`;
  const createEndpoint = `/api/v0/merchants/${merchantId}/create_strategy/`;
  const getEndpoint = `/api/v0/merchants/${merchantId}/strategies/${currentStrategyId}/strategy_by_id/`;
  const updateEndpoint = `/api/v0/merchants/${merchantId}/strategies/${currentStrategyId}/update_strategy/`;

  const currentNav = useRef(null);
  const [isDiscardModalOpen, setDiscardModalOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formError, setFormError] = useState();

  const unblock = history.block(nav => {
    currentNav.current = nav;
    if (isDirty) {
      setDiscardModalOpen(true);
      return false;
    }
    return true;
  });

  //  Set Default Values
  useEffect(() => {
    if (currentStrategyId) {
      api
        .get(getEndpoint)
        .then(resp => {
          const strategy = { ...resp.data.data[0] };
          setCurrentStrategy(strategy);
          setSelectedObjective(strategy.strategy_type);
          if (currentPage === 0 && strategy.strategy_is_evergreen) {
            setCurrentPage(1);
          }
        })
        .catch(() => {
          history.push(
            `/v2/merchant/${merchantId}/campaign-center/campaigns?strategy_group_ids=${strategyGroupId}`,
          );
        });
    }
  }, [currentPage]);

  //	Campaign Data
  useEffect(() => {
    const campaignEndpoint = `/api/v0/merchants/${merchantId}/strategy_groups/${strategyGroupId}/strategy_group_by_id/`;
    api.get(campaignEndpoint).then(resp => {
      const data = resp.data.data[0];
      setStrategyGroupData(data);
    });
  }, []);

  // Merchant Data
  useEffect(() => {
    api.get(merchantEndpoint).then(resp => {
      setMerchantPricingModel(resp.data.data[0].pricing_model);
    });
  }, []);

  const resetScroll = () => {
    const scrollContainer = document.getElementsByClassName('page-container');
    scrollContainer[0].scrollTop = 0;
  };

  const onObjectivesSubmit = payload => {
    if (currentStrategyId && isDirty) {
      api
        .put(updateEndpoint, { strategy: { strategy_type: payload } })
        .then(() => {
          setCurrentPage(1);
          setSelectedObjective(payload);
          setIsDirty(false);
        })
        .catch(err => setFormError(err.message));
    } else {
      setSelectedObjective(payload);
      setCurrentPage(1);
      setIsDirty(false);
      resetScroll();
    }
    setFormError(null);
  };

  const onSetUpSubmit = payload => {
    unblock();

    const onSubmit = () => {
      if (currentStrategy.strategy_is_evergreen) {
        history.push(
          `/v2/merchant/${merchantId}/campaign-center/campaigns?strategy_group_ids=${strategyGroupId}`,
        );
      } else {
        setCurrentPage(2);
        setIsDirty(false);
      }
      setFormError(null);
      resetScroll();
    };

    if (!isDirty && !formError) {
      onSubmit();
      return;
    }

    const apiCall = currentStrategyId
      ? Promise.resolve(
          api.put(updateEndpoint, { strategy: { ...payload, strategy_type: selectedObjective } }),
        )
      : Promise.resolve(
          api.post(createEndpoint, { strategy: { ...payload, strategy_type: selectedObjective } }),
        );

    return apiCall
      .then(resp => {
        const newStrategyId = resp.data.data[0].strategy_id;
        setCurrentStrategyId(newStrategyId);
        onSubmit();
      })
      .catch(err => {
        setFormError(err.message);
      });
  };

  const onTargetingSubmit = preventUpdate => {
    if (!strategyId && !preventUpdate) {
      api.put(updateEndpoint, { strategy: { strategy_is_enabled: true } }).then(res => {
        history.push(
          `/v2/merchant/${merchantId}/campaign-center/campaigns?strategy_group_ids=${strategyGroupId}`,
        );
      });
    } else {
      history.push(
        `/v2/merchant/${merchantId}/campaign-center/campaigns?strategy_group_ids=${strategyGroupId}`,
      );
    }
  };

  const navSteps = ['Set Up Campaign'];
  if (!currentStrategy.strategy_is_evergreen) {
    navSteps.push('Targeting');
    navSteps.unshift('Select Objective');
  }

  return (
    <div className="campaign-creation">
      <BreadcrumbNav
        crumbTrail={[
          {
            label: 'Campaign Center',
            href: `/v2/merchant/${merchantId}/campaign-center/campaigns`,
          },
          {
            label: strategyGroupData && strategyGroupData.strategy_group_name,
            href:
              strategyGroupData &&
              `/v2/merchant/${merchantId}/campaign-center/campaigns?strategy_group_ids=${strategyGroupData.strategy_group_id}`,
          },
          { label: `${strategyId ? `${currentStrategy.strategy_name}` : 'Create Campaign'}` },
        ]}
      />
      <div className="campaign-creation-header">
        <h2>{strategyId ? `Edit ${currentStrategy.strategy_name}` : 'Create Campaign'}</h2>
        {strategyGroupData && (
          <span className="subheader">under {strategyGroupData.strategy_group_name}</span>
        )}
      </div>
      <FormNav steps={navSteps} currentStep={currentPage} />
      <FormPage page={0} currentPage={currentPage}>
        <ObjectivesPage
          strategy={currentStrategy}
          merchantId={merchantId}
          strategyGroupId={strategyGroupId}
          onSubmit={onObjectivesSubmit}
          formError={formError}
          setIsDirty={setIsDirty}
          isDirty={isDirty}
        />
      </FormPage>
      <FormPage page={1} currentPage={currentPage}>
        <SetUpPage
          strategy={currentStrategy}
          strategyGroup={strategyGroupData}
          merchantId={merchantId}
          merchantPricingModel={merchantPricingModel}
          strategyGroupId={strategyGroupId}
          strategyId={currentStrategyId}
          strategyDefaultValues={strategyDefaultValues}
          setIsDirty={setIsDirty}
          onSubmit={onSetUpSubmit}
          formError={formError}
          handleBackButton={() => setCurrentPage(0)}
        />
      </FormPage>
      <FormPage page={2} currentPage={currentPage}>
        <TargetingPage
          strategy={currentStrategy}
          strategyId={currentStrategyId}
          strategyGroupId={strategyGroupId}
          onSubmit={onTargetingSubmit}
          formError={formError}
          merchantId={merchantId}
          handleBackButton={() => setCurrentPage(1)}
          setIsDirty={setIsDirty}
        />
      </FormPage>
      <Modal
        isShowing={isDiscardModalOpen}
        options={{
          identifier: 'strategy-creation',
          displayMsg: 'Discard changes?',
          displayTxt:
            "You haven't saved changes to your campaign. Are you sure you want to leave the page?",
          save: {
            txt: 'Discard Changes',
            callback: () => {
              unblock();
              currentNav.current && history.push(currentNav.current.pathname);
            },
          },
          cancel: {
            txt: 'Cancel',
            callback: () => setDiscardModalOpen(false),
          },
        }}
      />
    </div>
  );
};

StrategyCreation.propTypes = {};

StrategyCreation.defaultProps = {};

export default StrategyCreation;
